import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Edit2, Terminal } from 'lucide-react';
import { articleService, smeService, editorService } from '../services/api';
import { useBrandedDialog } from './BrandedDialog';
import './PromptManager.css';

const PromptManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { alert, confirm, toast } = useBrandedDialog();

  // Read tab parameter from URL query string
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab') || 'system';

  const [activeTab, setActiveTab] = useState(urlTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Prompt states
  const [mergerPrompt, setMergerPrompt] = useState('');
  const [topicResearcherPrompt, setTopicResearcherPrompt] = useState('');
  const [smes, setSmes] = useState([]);
  const [editors, setEditors] = useState([]);

  // Search & Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory] = useState('All Categories');
  const [smePage, setSmePage] = useState(1);
  const smePageSize = 8;

  // Modal Editor state
  const [editingPrompt, setEditingPrompt] = useState(null); // { type: 'system'|'sme'|'editor', id: string, name: string, prompt: string }
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [revertingPrompt, setRevertingPrompt] = useState(false);

  const formatPromptDate = (item) => {
    const rawDate = item?.updatedAt || item?.updated_at || item?.updatedDate || item?.createdAt || item?.created_at || item?.createdDate;
    if (!rawDate) return '';
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Sync active tab state with URL parameter changes
  useEffect(() => {
    setActiveTab(urlTab === 'corrections' ? 'system' : urlTab);
  }, [urlTab]);

  const [mainPage, setMainPage] = useState(1);
  const mainPageSize = 5;

  useEffect(() => {
    setMainPage(1);
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [basePromptResponse, smeResponse, editorResponse] = await Promise.all([
        articleService.getBaseSystemPrompt(),
        smeService.getAll(),
        editorService.getAll(),
      ]);

      setMergerPrompt(basePromptResponse.data.merger_prompt || basePromptResponse.data.raw_prompt || '');
      setTopicResearcherPrompt(basePromptResponse.data.topic_researcher_prompt || '');
      setSmes(smeResponse.data || []);
      setEditors(editorResponse.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle Editing Modal Trigger
  const openEditModal = (type, item) => {
    if (type === 'merger') {
      setEditingPrompt({
        type: 'merger',
        name: 'Merger Base Prompt (Editor)',
        description: 'Used as the base editor prompt to synthesize multiple expert viewpoints.',
        prompt: mergerPrompt
      });
    } else if (type === 'researcher') {
      setEditingPrompt({
        type: 'researcher',
        name: 'Topic Researcher Base Prompt (SME)',
        description: 'Used as the base prompt for SME topic research fanning out.',
        prompt: topicResearcherPrompt
      });
    } else if (type === 'sme') {
      setEditingPrompt({
        type: 'sme',
        id: item._id,
        name: `${item.name} Prompt`,
        description: `Domain-specific prompt used by SME persona (${item.type}).`,
        prompt: item.prompt || ''
      });
    } else if (type === 'editor') {
      setEditingPrompt({
        type: 'editor',
        id: item._id,
        name: `${item.name} Prompt`,
        description: `Stylistic writing prompt used by Editor persona (${item.style}).`,
        prompt: item.prompt || ''
      });
    }
  };

  const handleSavePromptEdit = async () => {
    if (!editingPrompt) return;
    try {
      setSavingPrompt(true);
      setError('');
      setSuccess('');

      if (editingPrompt.type === 'merger') {
        const response = await articleService.updateBaseSystemPrompt({
          merger_prompt: editingPrompt.prompt,
          topic_researcher_prompt: topicResearcherPrompt,
        });
        setMergerPrompt(response?.data?.merger_prompt || editingPrompt.prompt);
      } else if (editingPrompt.type === 'researcher') {
        const response = await articleService.updateBaseSystemPrompt({
          merger_prompt: mergerPrompt,
          topic_researcher_prompt: editingPrompt.prompt,
        });
        setTopicResearcherPrompt(response?.data?.topic_researcher_prompt || editingPrompt.prompt);
      } else if (editingPrompt.type === 'sme') {
        await smeService.updatePrompt(editingPrompt.id, { prompt: editingPrompt.prompt });
        // Refresh SMEs
        const smeResponse = await smeService.getAll();
        setSmes(smeResponse.data || []);
      } else if (editingPrompt.type === 'editor') {
        await editorService.updatePrompt(editingPrompt.id, { prompt: editingPrompt.prompt });
        // Refresh Editors
        const editorResponse = await editorService.getAll();
        setEditors(editorResponse.data || []);
      }

      const successMessage = `"${editingPrompt.name}" saved successfully.`;
      setSuccess(successMessage);
      toast({ title: 'Prompt saved', message: successMessage, tone: 'success' });
      setEditingPrompt(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to save prompt template';
      setError(errorMessage);
      alert({ title: 'Failed to save prompt', message: errorMessage, tone: 'danger' });
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleRevertPromptEdit = async () => {
    if (!editingPrompt) return;
    const confirmed = await confirm({
      title: 'Revert prompt?',
      message: 'Revert this prompt to its original version? Your edits will be lost.',
      confirmLabel: 'Revert',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      setRevertingPrompt(true);
      setError('');
      setSuccess('');

      if (editingPrompt.type === 'merger' || editingPrompt.type === 'researcher') {
        const response = await articleService.revertBaseSystemPrompt();
        const newMerger = response?.data?.merger_prompt || response?.data?.prompt || '';
        const newResearcher = response?.data?.topic_researcher_prompt || '';

        setMergerPrompt(newMerger);
        setTopicResearcherPrompt(newResearcher);

        if (editingPrompt.type === 'merger') {
          setEditingPrompt(prev => ({ ...prev, prompt: newMerger }));
        } else {
          setEditingPrompt(prev => ({ ...prev, prompt: newResearcher }));
        }
      } else if (editingPrompt.type === 'sme') {
        const response = await smeService.revertPrompt(editingPrompt.id);
        const revertedPrompt = response?.data?.prompt || '';
        setSmes(prev => prev.map(sme => (
          sme._id === editingPrompt.id ? { ...sme, prompt: revertedPrompt } : sme
        )));
        setEditingPrompt(prev => ({ ...prev, prompt: revertedPrompt }));
      } else if (editingPrompt.type === 'editor') {
        const response = await editorService.revertPrompt(editingPrompt.id);
        const revertedPrompt = response?.data?.prompt || '';
        setEditors(prev => prev.map(editor => (
          editor._id === editingPrompt.id ? { ...editor, prompt: revertedPrompt } : editor
        )));
        setEditingPrompt(prev => ({ ...prev, prompt: revertedPrompt }));
      }

      const successMessage = `"${editingPrompt.name}" reverted to original.`;
      setSuccess(successMessage);
      toast({ title: 'Prompt reverted', message: successMessage, tone: 'success' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to revert prompt';
      setError(errorMessage);
      alert({ title: 'Failed to revert prompt', message: errorMessage, tone: 'danger' });
    } finally {
      setRevertingPrompt(false);
    }
  };

  // Render System Prompts items
  const systemPromptsList = [
    {
      key: 'merger',
      title: 'Merger Base Prompt (Editor)',
      category: 'Core',
      updatedAt: '',
      updatedBy: 'Kabir Malhotra',
      snippet: mergerPrompt.substring(0, 140),
      type: 'merger'
    },
    {
      key: 'researcher',
      title: 'Topic Researcher Base Prompt (SME)',
      category: 'Research',
      updatedAt: '',
      updatedBy: 'Sneha Patel',
      snippet: topicResearcherPrompt.substring(0, 140),
      type: 'researcher'
    }
  ];

  const getFilteredItems = () => {
    let list = [];
    if (activeTab === 'system') {
      list = systemPromptsList;
    } else if (activeTab === 'sme') {
      list = smes.map(sme => ({
        key: sme._id,
        title: sme.name,
        category: sme.type || 'SME',
        updatedAt: formatPromptDate(sme),
        updatedBy: 'System',
        snippet: (sme.prompt || '').substring(0, 140),
        type: 'sme',
        raw: sme
      }));
    } else if (activeTab === 'editor') {
      list = editors.map(editor => ({
        key: editor._id,
        title: editor.name,
        category: editor.style || 'Editor',
        updatedAt: formatPromptDate(editor),
        updatedBy: 'System',
        snippet: (editor.prompt || '').substring(0, 140),
        type: 'editor',
        raw: editor
      }));
    } else {
      list = [];
    }

    // Apply Search
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      list = list.filter(item => 
        item.title.toLowerCase().includes(query) || 
        item.snippet.toLowerCase().includes(query)
      );
    }

    // Apply Category Filter
    if (selectedCategory !== 'All Categories') {
      list = list.filter(item => item.category === selectedCategory);
    }

    return list;
  };

  const filteredItems = getFilteredItems();
  const totalItems = filteredItems.length;
  const startIndex = totalItems === 0 ? 0 : (mainPage - 1) * mainPageSize;
  const endIndex = Math.min(mainPage * mainPageSize, totalItems);
  const paginatedItems = filteredItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalItems / mainPageSize);

  // Avatar Initials helper
  const getInitials = (name) => {
    if (!name) return 'S';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="app-loading-container">
        <div className="app-loading-spinner" />
        <p>Loading prompt manager...</p>
      </div>
    );
  }

  return (
    <div className="prompts-workspace-grid">
      {/* Left Column: List and Filters */}
      <div className="prompts-main-pane">
        
        {/* Workspace Title Block */}
        <div className="prompts-header-banner">
          <div className="prompts-header-left">
            <div className="prompts-banner-icon">
              <Terminal size={22} strokeWidth={1.5} />
            </div>
          <div>
            <h2>Prompt Manager</h2>
            <p>Manage and organize system, SME, and editor prompts</p>
          </div>
        </div>
      </div>

        {error && <div className="form-error" style={{ marginBottom: '1.25rem' }}>{error}</div>}
        {success && <div className="form-success" style={{ marginBottom: '1.25rem' }}>{success}</div>}

        {/* Tab segmented controller */}
        <div className="prompts-tab-segmented">
          <button 
            type="button" 
            className={`prompts-tab-btn ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => { setActiveTab('system'); navigate('/prompts?tab=system'); }}
          >
            System Prompts
          </button>
          <button 
            type="button" 
            className={`prompts-tab-btn ${activeTab === 'sme' ? 'active' : ''}`}
            onClick={() => { setActiveTab('sme'); navigate('/prompts?tab=sme'); }}
          >
            SME Prompts
          </button>
          <button
            type="button"
            className={`prompts-tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
            onClick={() => { setActiveTab('editor'); navigate('/prompts?tab=editor'); }}
          >
            Editor Prompts
          </button>
        </div>

        {/* Filter controls bar */}
        <div className="prompts-filter-bar" style={{ justifyContent: 'flex-end' }}>
          <div className="prompts-search-wrapper">
            <Search size={16} className="prompts-search-icon" />
            <input 
              type="text"
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="prompts-search-input"
            />
          </div>
        </div>

        {/* Prompts list vertical stack */}
        <div className="prompts-list-stack">
          {paginatedItems.length === 0 ? (
            <div className="prompts-empty-state">No matching prompt templates found.</div>
          ) : (
            paginatedItems.map(item => (
              <div key={item.key} className="prompts-list-card">
                <div className="prompts-card-icon-container">
                  <Terminal size={18} color="var(--accent)" />
                </div>
                
                <div className="prompts-card-details">
                  <div className="prompts-card-details-top">
                    <h4>{item.title}</h4>
                  </div>
                  {item.updatedAt && (
                    <p className="prompts-card-meta">
                      Last updated: {item.updatedAt}
                    </p>
                  )}
                  <p className="prompts-card-snippet">{item.snippet}</p>
                </div>

                <div className="prompts-card-actions">
                  <button className="prompts-action-icon-btn" onClick={() => openEditModal(item.type, item.raw)}>
                    <Edit2 size={15} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination footer */}
        <div className="prompts-pagination">
          <span className="prompts-pagination-summary">
            Showing {totalItems === 0 ? 0 : startIndex + 1} to {endIndex} of {totalItems} prompts
          </span>
          <div className="prompts-page-controls">
            <button 
              className={`page-btn ${mainPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setMainPage(p => Math.max(p - 1, 1))}
              disabled={mainPage === 1}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
              <button 
                key={pageNumber} 
                className={`page-btn ${mainPage === pageNumber ? 'active' : ''}`}
                onClick={() => setMainPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button 
              className={`page-btn ${mainPage >= totalPages ? 'disabled' : ''}`} 
              onClick={() => setMainPage(p => Math.min(p + 1, totalPages))}
              disabled={mainPage >= totalPages}
            >
              &gt;
            </button>
          </div>
        </div>

      </div>

      {/* Right Column: SME Prompts Summary list */}
      <div className="prompts-side-pane">
        <div className="prompts-side-header">
          <h3>SME Prompts</h3>
          <p>Domain-specific prompts used by SMEs</p>
        </div>

        <div className="prompts-side-list">
          {smes.slice((smePage - 1) * smePageSize, smePage * smePageSize).map(sme => {
            const initials = getInitials(sme.name);
            const avatarColor = ['#dcfce7', '#f3e8ff', '#ffe4e6', '#ffedd5', '#e0f2fe'][sme.name.length % 5];
            const textColor = ['#15803d', '#7e22ce', '#be123c', '#c2410c', '#0369a1'][sme.name.length % 5];
            
            return (
              <div key={sme._id} className="prompts-side-card">
                <div className="prompts-side-avatar" style={{ background: avatarColor, color: textColor }}>
                  {initials}
                </div>
                
                <div className="prompts-side-info">
                  <h4>{sme.name}</h4>
                  <p className="prompts-side-type">{sme.type || 'Specialist'}</p>
                  {formatPromptDate(sme) && (
                    <p className="prompts-side-meta">Last updated: {formatPromptDate(sme)}</p>
                  )}
                </div>

                <div className="prompts-side-actions">
                  <button className="prompts-action-icon-btn" onClick={() => openEditModal('sme', sme)}>
                    <Edit2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Paginated Counter indicator */}
        <div className="sme-side-pagination" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.82rem',
          color: '#5b7466',
          marginTop: '0.5rem',
          padding: '0.25rem 0'
        }}>
          <span style={{ fontWeight: '600' }}>
            {`${(smePage - 1) * smePageSize + 1}-${Math.min(smePage * smePageSize, smes.length)} out of ${smes.length}`}
          </span>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button 
              className="page-btn-sm" 
              onClick={() => setSmePage(p => Math.max(p - 1, 1))} 
              disabled={smePage === 1}
              style={{
                width: '28px',
                height: '28px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                background: '#ffffff',
                cursor: smePage === 1 ? 'not-allowed' : 'pointer',
                opacity: smePage === 1 ? 0.4 : 1,
                fontSize: '0.85rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              &lt;
            </button>
            <button 
              className="page-btn-sm" 
              onClick={() => setSmePage(p => Math.min(p + 1, Math.ceil(smes.length / smePageSize)))} 
              disabled={smePage >= Math.ceil(smes.length / smePageSize)}
              style={{
                width: '28px',
                height: '28px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                background: '#ffffff',
                cursor: smePage >= Math.ceil(smes.length / smePageSize) ? 'not-allowed' : 'pointer',
                opacity: smePage >= Math.ceil(smes.length / smePageSize) ? 0.4 : 1,
                fontSize: '0.85rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              &gt;
            </button>
          </div>
        </div>

        <button 
          className="btn btn-outline prompts-side-footer-btn"
          onClick={() => { setActiveTab('sme'); navigate('/prompts?tab=sme'); }}
        >
          View All SME Prompts &rarr;
        </button>
      </div>

      {/* Floating Prompt Editor Modal */}
      {editingPrompt && (
        <div className="prompt-modal-backdrop" onClick={() => setEditingPrompt(null)}>
          <div className="prompt-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="prompt-modal-header">
              <div>
                <h3>{editingPrompt.name}</h3>
                <p>{editingPrompt.description}</p>
              </div>
            </div>

            <textarea
              className="prompt-textarea prompt-modal-textarea"
              value={editingPrompt.prompt}
              onChange={(e) => setEditingPrompt(prev => ({ ...prev, prompt: e.target.value }))}
              rows="16"
            />

            <div className="prompt-modal-actions">
              <button 
                className="btn btn-outline" 
                onClick={() => setEditingPrompt(null)}
                disabled={savingPrompt || revertingPrompt}
              >
                Cancel
              </button>
              <button 
                className="btn btn-outline" 
                onClick={handleRevertPromptEdit}
                disabled={savingPrompt || revertingPrompt}
              >
                {revertingPrompt ? 'Reverting...' : 'Revert to original'}
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSavePromptEdit}
                disabled={savingPrompt || revertingPrompt}
              >
                {savingPrompt ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptManager;

